import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '@/locales/strings';
import { reportError } from '@/utils/errorReporter';

/**
 * ScreenBoundary — per-route error boundary. Wraps a single screen
 * so a render crash doesn't take down the whole app; the user sees
 * a humane editorial fallback with a "Try again" action that
 * either re-renders the screen (light reset) or navigates back to
 * /home (hard reset) depending on the failure mode.
 *
 * Paired with the top-level ErrorBoundary in App.jsx — the global
 * one still catches anything that slips past route-level attempts,
 * but the route-level boundary means a crash in Profile doesn't
 * also blank the session the user was about to run.
 *
 * Class-component-only so it can use componentDidCatch /
 * getDerivedStateFromError; thin functional wrapper handles
 * location + navigate + locale.
 */
class ScreenBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, locationKey: props.locationKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the console so devs still see the stack in the field.
    console.error('[ScreenBoundary]', error, info?.componentStack);
    reportError(error, { componentStack: info?.componentStack, scope: 'ScreenBoundary' });
  }

  componentDidUpdate(prevProps) {
    // Route change from outside the boundary (e.g. BottomNav tap)
    // clears the error state so the new screen renders fresh
    // instead of showing the fallback from the previous crash.
    if (prevProps.locationKey !== this.props.locationKey && this.state.error) {
      this.setState({ error: null, locationKey: this.props.locationKey });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { lang, onHome } = this.props;
    return (
      <div
        role="alert"
        style={{
          position: 'fixed', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: 'max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))',
          background: 'var(--bg, #F4F1ED)',
          color: 'var(--ink, #2d1f26)',
          textAlign: 'center',
          gap: 14,
        }}
      >
        <div style={{
          font: '600 10.5px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
        }}>
          {t('screen_boundary_eyebrow', lang)}
        </div>
        <h1 style={{
          font: '500 24px/1.2 var(--font-title, Fraunces), serif',
          fontStyle: 'italic',
          margin: 0,
          textWrap: 'balance',
          maxWidth: 320,
        }}>
          {t('screen_boundary_title', lang)}
        </h1>
        <p style={{
          font: '400 14px/1.5 var(--font-body, Manrope)',
          color: 'var(--ink-dim, #6b5560)',
          margin: 0,
          maxWidth: 320,
          textWrap: 'pretty',
        }}>
          {t('screen_boundary_body', lang)}
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            style={{
              minHeight: 44,
              padding: '12px 20px',
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 14%, transparent)',
              background: 'transparent',
              color: 'var(--ink, #2d1f26)',
              font: '600 13px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
            }}
          >
            {t('screen_boundary_retry', lang)}
          </button>
          <button
            type="button"
            onClick={onHome}
            style={{
              minHeight: 44,
              padding: '12px 20px',
              borderRadius: 999,
              border: 'none',
              background: 'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
              color: '#fff',
              font: '600 13px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            {t('screen_boundary_home', lang)}
          </button>
        </div>
      </div>
    );
  }
}

export default function ScreenBoundary({ children, lang = 'en' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = React.useCallback(() => {
    navigate('/home', { replace: true });
  }, [navigate]);
  return (
    <ScreenBoundaryClass
      locationKey={location.key}
      lang={lang}
      onHome={onHome}
    >
      {children}
    </ScreenBoundaryClass>
  );
}
